import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Webhook,
  ShieldCheck,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Loader2,
  KeyRound,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SFHealthKPIs, SFHistoryTab } from "../components/SFHealthAndHistory";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("ro-RO");
  } catch {
    return s;
  }
};

const StatusBadge = ({ enabled, labelOn = "Activ", labelOff = "Dezactivat" }) =>
  enabled ? (
    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="badge-status-on">
      <CheckCircle2 className="w-3 h-3 mr-1" /> {labelOn}
    </Badge>
  ) : (
    <Badge variant="secondary" className="bg-slate-100 text-slate-700" data-testid="badge-status-off">
      <XCircle className="w-3 h-3 mr-1" /> {labelOff}
    </Badge>
  );

export default function SFIntegration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [dlq, setDlq] = useState([]);
  const [showDlqResolved, setShowDlqResolved] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  // Local editable form state (mirrors config)
  const [form, setForm] = useState({
    polling_interval_minutes: 30,
    allowed_ips_cidr: "",
    replay_window_seconds: 300,
    rate_limit_per_sec: 100,
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, dlqRes] = await Promise.all([
        axios.get(`${API}/admin/sf-integration/config`),
        axios.get(`${API}/admin/sf-integration/dlq`, {
          params: { resolved: showDlqResolved },
        }),
      ]);
      setData(cfgRes.data);
      setDlq(dlqRes.data?.data || []);
      const c = cfgRes.data?.config || {};
      setForm({
        polling_interval_minutes: c.polling_interval_minutes ?? 30,
        allowed_ips_cidr: c.allowed_ips_cidr ?? "",
        replay_window_seconds: c.replay_window_seconds ?? 300,
        rate_limit_per_sec: c.rate_limit_per_sec ?? 100,
      });
    } catch (e) {
      toast.error("Eroare la încărcare: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [showDlqResolved]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateConfig = async (payload, successMsg) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/sf-integration/config`, payload);
      toast.success(successMsg || "Configurație actualizată");
      await fetchAll();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleWebhook = async (val) => {
    await updateConfig(
      { webhook_enabled: val },
      val ? "Webhook activat" : "Webhook dezactivat"
    );
  };

  const togglePolling = async (val) => {
    await updateConfig(
      { polling_enabled: val },
      val ? "Polling activat" : "Polling dezactivat"
    );
  };

  const saveSettings = async () => {
    await updateConfig(
      {
        polling_interval_minutes: Number(form.polling_interval_minutes) || 30,
        allowed_ips_cidr: form.allowed_ips_cidr || null,
        replay_window_seconds: Number(form.replay_window_seconds) || 300,
        rate_limit_per_sec: Number(form.rate_limit_per_sec) || 100,
      },
      "Setări salvate"
    );
  };

  const rotateHmac = async () => {
    setRotating(true);
    try {
      await axios.put(`${API}/admin/sf-integration/config`, {
        rotate_hmac_version: true,
      });
      toast.success("Versiune HMAC rotată cu succes");
      setRotateOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setRotating(false);
    }
  };

  const retryDlq = async (dlqId) => {
    try {
      await axios.post(`${API}/admin/sf-integration/dlq/${dlqId}/retry`);
      toast.success("Retry programat");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-6 flex items-center gap-3 text-slate-500" data-testid="sf-integration-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Se încarcă...
      </div>
    );
  }

  const cfg = data.config;
  const stats = data.stats;
  const hasPrevVersion = cfg.active_hmac_versions && cfg.active_hmac_versions.length > 1;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="sf-integration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Webhook className="w-6 h-6 text-indigo-600" /> Integrare LIVE Salesforce
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Webhook receiver semnat HMAC, idempotent, cu DLQ și rotație de chei.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Reîmprospătează
        </Button>
      </div>

      {/* Iter27 — Sănătatea integrării (top section, 4 KPI auto-refresh 30s) */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Sănătatea integrării
        </h2>
        <SFHealthKPIs refreshKey={data?.config?.webhook_enabled ? 1 : 0} />
      </div>

      <Tabs defaultValue="config" className="w-full">
        <TabsList>
          <TabsTrigger value="config" data-testid="sf-tab-config">Configurare</TabsTrigger>
          <TabsTrigger value="history" data-testid="sf-tab-history">Istoric & raportare</TabsTrigger>
        </TabsList>
        <TabsContent value="config" className="mt-4 space-y-6">

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-webhook-status">
          <CardHeader className="pb-2">
            <CardDescription>Status Webhook</CardDescription>
          </CardHeader>
          <CardContent>
            <StatusBadge enabled={cfg.webhook_enabled} />
            <div className="text-xs text-slate-500 mt-2">
              {cfg.secret_configured ? (
                <span className="text-emerald-700 flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Secret v{cfg.hmac_secret_version} configurat
                </span>
              ) : (
                <span className="text-rose-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Secret v{cfg.hmac_secret_version} LIPSĂ
                </span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-polling-status">
          <CardHeader className="pb-2">
            <CardDescription>Status Polling</CardDescription>
          </CardHeader>
          <CardContent>
            <StatusBadge enabled={cfg.polling_enabled} />
            <div className="text-xs text-slate-500 mt-2">
              Interval: {cfg.polling_interval_minutes} min
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-events-24h">
          <CardHeader className="pb-2">
            <CardDescription>Evenimente 24h</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-indigo-700" data-testid="events-24h-count">
              {stats.events_24h}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Ultimul: {fmtDate(stats.last_event_received_at)}{" "}
              {stats.last_event_status && (
                <Badge variant="outline" className="ml-1 text-xs">
                  {stats.last_event_status}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-dlq-count">
          <CardHeader className="pb-2">
            <CardDescription>DLQ Pending</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`text-3xl font-bold ${
                stats.dlq_pending > 0 ? "text-rose-700" : "text-slate-400"
              }`}
              data-testid="dlq-pending-count"
            >
              {stats.dlq_pending}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Evenimente nereușite definitiv
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toggles */}
      <Card data-testid="config-toggles-card">
        <CardHeader>
          <CardTitle className="text-lg">Activare</CardTitle>
          <CardDescription>
            Webhook-ul rămâne DEZACTIVAT până când admin-ul confirmă explicit. Secret-ul HMAC
            trebuie configurat (`SF_WEBHOOK_SECRET_V{cfg.hmac_secret_version}`) înainte de
            activare.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div>
              <div className="font-medium">Webhook receiver activ</div>
              <div className="text-xs text-slate-500">
                POST /api/webhooks/salesforce acceptă evenimente HMAC-semnate
              </div>
            </div>
            <Switch
              checked={cfg.webhook_enabled}
              onCheckedChange={toggleWebhook}
              disabled={saving}
              data-testid="toggle-webhook"
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div>
              <div className="font-medium">Polling SF activ (fallback)</div>
              <div className="text-xs text-slate-500">
                Job periodic care interoghează SF pentru evenimente pierdute
              </div>
            </div>
            <Switch
              checked={cfg.polling_enabled}
              onCheckedChange={togglePolling}
              disabled={saving}
              data-testid="toggle-polling"
            />
          </div>
        </CardContent>
      </Card>

      {/* Settings form */}
      <Card data-testid="config-settings-card">
        <CardHeader>
          <CardTitle className="text-lg">Setări tehnice</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="rate_limit">Rate limit (req / sec)</Label>
            <Input
              id="rate_limit"
              type="number"
              min="1"
              value={form.rate_limit_per_sec}
              onChange={(e) =>
                setForm((f) => ({ ...f, rate_limit_per_sec: e.target.value }))
              }
              data-testid="input-rate-limit"
            />
          </div>
          <div>
            <Label htmlFor="replay_window">Fereastră replay (secunde)</Label>
            <Input
              id="replay_window"
              type="number"
              min="30"
              value={form.replay_window_seconds}
              onChange={(e) =>
                setForm((f) => ({ ...f, replay_window_seconds: e.target.value }))
              }
              data-testid="input-replay-window"
            />
          </div>
          <div>
            <Label htmlFor="polling_interval">Interval polling (minute)</Label>
            <Input
              id="polling_interval"
              type="number"
              min="1"
              value={form.polling_interval_minutes}
              onChange={(e) =>
                setForm((f) => ({ ...f, polling_interval_minutes: e.target.value }))
              }
              data-testid="input-polling-interval"
            />
          </div>
          <div>
            <Label htmlFor="allowed_ips">IP-uri permise (CIDR, comma-separated)</Label>
            <Input
              id="allowed_ips"
              placeholder="goal (toate) sau 13.110.0.0/15,52.88.0.0/13"
              value={form.allowed_ips_cidr || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, allowed_ips_cidr: e.target.value }))
              }
              data-testid="input-allowed-ips"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvează setările
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* HMAC rotation */}
      <Card data-testid="hmac-rotation-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> Rotație HMAC
          </CardTitle>
          <CardDescription>
            Versiunea activă: <Badge>v{cfg.hmac_secret_version}</Badge>
            {hasPrevVersion && (
              <>
                {" "}
                + <Badge variant="outline">v{cfg.hmac_secret_version - 1}</Badge> (legacy, încă acceptat)
              </>
            )}
            . Pentru rotație zero-downtime: setează `SF_WEBHOOK_SECRET_V{cfg.hmac_secret_version + 1}`
            în env, apoi rotește. Versiunea anterioară rămâne validă pentru tranziție.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setRotateOpen(true)}
            data-testid="rotate-hmac-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Rotește la v{cfg.hmac_secret_version + 1}
          </Button>
          <div className="text-xs text-slate-500">
            Versiuni active acceptate: [{cfg.active_hmac_versions?.join(", ")}]
          </div>
        </CardContent>
      </Card>

      {/* DLQ */}
      <Card data-testid="dlq-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" /> Dead Letter Queue
            </CardTitle>
            <CardDescription>
              Evenimente eșuate definitiv ({"≥3 încercări"}). Pot fi reluate manual.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="dlq-resolved-toggle" className="text-xs">
              Afișează rezolvate
            </Label>
            <Switch
              id="dlq-resolved-toggle"
              checked={showDlqResolved}
              onCheckedChange={setShowDlqResolved}
              data-testid="toggle-dlq-resolved"
            />
          </div>
        </CardHeader>
        <CardContent>
          {dlq.length === 0 ? (
            <div className="text-sm text-slate-500 p-6 text-center" data-testid="dlq-empty">
              {showDlqResolved
                ? "Nicio intrare DLQ rezolvată."
                : "DLQ este gol. Niciun eveniment eșuat."}
            </div>
          ) : (
            <Table data-testid="dlq-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Event ID</TableHead>
                  <TableHead>Eșuat la</TableHead>
                  <TableHead>Tentative</TableHead>
                  <TableHead>Eroare</TableHead>
                  <TableHead>Rezolvat</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dlq.map((d) => (
                  <TableRow key={d.id} data-testid={`dlq-row-${d.id}`}>
                    <TableCell className="font-mono text-xs">{d.original_event_id}</TableCell>
                    <TableCell className="text-xs">{fmtDate(d.failed_at)}</TableCell>
                    <TableCell>{d.attempts}</TableCell>
                    <TableCell className="text-xs text-rose-700 max-w-md truncate">
                      {d.last_error}
                    </TableCell>
                    <TableCell>
                      {d.resolved ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Da</Badge>
                      ) : (
                        <Badge variant="outline">Nu</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!d.resolved && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => retryDlq(d.id)}
                          data-testid={`dlq-retry-${d.id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" /> Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <SFHistoryTab />
        </TabsContent>
      </Tabs>

      {/* Rotate confirm dialog */}
      <Dialog open={rotateOpen} onOpenChange={setRotateOpen}>
        <DialogContent data-testid="rotate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5" /> Confirmă rotația HMAC
            </DialogTitle>
            <DialogDescription>
              Vei roti versiunea curentă de la v{cfg.hmac_secret_version} la v
              {cfg.hmac_secret_version + 1}.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600 -mt-2">
            Pentru a NU bloca producerea, asigură-te că:
            <ul className="list-disc list-inside mt-2 text-xs space-y-1">
              <li>
                Variabila <code>SF_WEBHOOK_SECRET_V{cfg.hmac_secret_version + 1}</code> este
                setată în env și backend-ul a fost restartat.
              </li>
              <li>
                Versiunea anterioară (v{cfg.hmac_secret_version}) rămâne acceptată automat
                până la următoarea rotație.
              </li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotateOpen(false)} data-testid="rotate-cancel">
              Anulează
            </Button>
            <Button onClick={rotateHmac} disabled={rotating} data-testid="rotate-confirm">
              {rotating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmă rotația
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
